import React, {Component, ReactElement} from 'react'
import {ColumnFlexContainer, FullWidthColumnFlexContainer} from '../../../../styledComponents/FlexComponents'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {Box} from '@mui/material'
import styled from 'styled-components'
import {Colors, FontSize, FontWeights} from '../../../../styledComponents/Constants'
import {FIRST_LOGIN_REFERRER_ID, OWNERSHIP_TRANSFER_STATUS, ROLE_FOR_GUEST} from '../../../../CodeMaster'
import {Nullable} from '../../../../app/TypeHelpers'
import {NatOwnershipTransfersRepo} from '../repository/NatOwnershipTransfersRepo'
import {NatOwnershipTransferApplyLocationState} from './NatOwnershipTransfersCompleteInterface'
import {formatDate} from '../../../../formatters/formatDate'
import {GuestApplyButton, GuestBackButton} from '../common/NatOwnershipTransfersCommon'
import {
    createGuestWithErrorHandlingPage,
    GuestWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createGuestWithErrorHandlingPage'
import {NatGetOwnershipTransferNewMemberResponse} from '../repository/NatGetOwnershipTransferNewMember'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    roleId: ROLE_FOR_GUEST
    location: Location<NatOwnershipTransferApplyLocationState>
}

interface States {
    isPossibleSutekiNetRegister: boolean
    isLoadCompleted: boolean
}


class NatOwnershipTransfersCompletePage extends Component<Props & GuestWithErrorHandlingPagePropsSet, States> {
    constructor(props: Readonly<Props & GuestWithErrorHandlingPagePropsSet>) {
        super(props)
        this.state = {
            isPossibleSutekiNetRegister: false,
            isLoadCompleted: false,
        }
        this.handleConfirmApplication = this.handleConfirmApplication.bind(this)
        this.handleRegisterSutekiUser = this.handleRegisterSutekiUser.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {condominiumCd, ownershipTransfersRepo, roleId, eventEmitter} = this.props

        await eventEmitter.showLoading()
        try {
            if (roleId === ROLE_FOR_GUEST.NEW_MEMBER) {
                const getSubmitStatusResult = await this.props.asyncCallFunction(
                    ownershipTransfersRepo.getOwnershipTransfersSubmitStatus.bind(
                        ownershipTransfersRepo, condominiumCd,
                    ),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    })

                if (getSubmitStatusResult) {
                    let newMemberAuthenticationKey = null
                    let newMemberMailAddress = null
                    let isPossibleSutekiNetRegister = false
                    const newMemberInfo: Nullable<NatGetOwnershipTransferNewMemberResponse>
                        = await this.props.asyncCallFunction(
                        ownershipTransfersRepo.getOwnershipTransferNewMember.bind(
                            ownershipTransfersRepo, condominiumCd, getSubmitStatusResult.details.applyId),
                        (httpErrorOrNetworkError) => {
                            eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                        })
                    if (newMemberInfo) {
                        newMemberAuthenticationKey =
                            newMemberInfo.details.applyInfo.condominiumInfo.authenticationKey
                        newMemberMailAddress =
                            newMemberInfo.details.newMemberInfo?.mailAddress
                        isPossibleSutekiNetRegister =
                            (newMemberInfo.details.applyInfo.applyStatus === OWNERSHIP_TRANSFER_STATUS.COMPLETE.value)
                            && (newMemberAuthenticationKey !== null)
                    }
                    this.setState({
                        isPossibleSutekiNetRegister: isPossibleSutekiNetRegister,
                    })
                }
            }
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({
                    isLoadCompleted: true,
                },
            )
        }
    }

    handleConfirmApplication(): void {
        const {navigate} = this.props
        let path
        switch (this.props.roleId) {
            case ROLE_FOR_GUEST.INTERMEDIARY:
                path = `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/intermediary/confirm/${this.props.location.state.applyId}`
                break
            case ROLE_FOR_GUEST.OLD_MEMBER:
                path = `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/old-member/confirm/${this.props.location.state.applyId}`
                break
            default:
                path = `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/new-member/confirm/${this.props.location.state.applyId}`
                break
        }
        navigate(path, {
            replace: true,
            state: {
                isCompleteFlow: true,
            },
        })
    }

    handleRegisterSutekiUser(): void {
        this.props.navigate(
            `/resident/firstLoginPolicyAgree?referrerId=${FIRST_LOGIN_REFERRER_ID.OWNERSHIP_TRANSFER.value}`,
        )
    }

    renderRegistrationButton(): Nullable<ReactElement> {
        if (this.props.roleId !== ROLE_FOR_GUEST.NEW_MEMBER) {
            return (<></>)
        }

        return (
            <>
                <GuestApplyButton
                    disabled={!this.state.isPossibleSutekiNetRegister}
                    onClick={this.handleRegisterSutekiUser}
                >
                    <FullWidthColumnFlexContainer>
                        <div>
                            素敵ネットのユーザ登録をする
                        </div>
                    </FullWidthColumnFlexContainer>
                </GuestApplyButton>
                <Box height={'30px'}/>
            </>
        )
    }

    renderConfirmButton(): ReactElement {
        return (<>
            <GuestBackButton onClick={this.handleConfirmApplication}>
                申請内容を確認する
            </GuestBackButton>
            <BlockSpacer height={'30px'}/>
        </>)
    }

    renderIntermediary(): ReactElement {
        return (<>
            {(this.props.roleId === ROLE_FOR_GUEST.INTERMEDIARY) && this.props.location.state.accessExpirationDate &&
                <>
                    <Box height={'15px'}/>
                    <CompleteMessageSub>
                        旧組合員様・新組合員様に<br/>
                        「組合員資格変更届のご案内」をお渡しいただき、<br/>
                        決済の場で組合員資格変更届を入力いただきますよう<br/>
                        お願いいたします。<br/>
                        申請内容は{formatDate(this.props.location.state.accessExpirationDate)}までご確認いただけます。
                    </CompleteMessageSub>
                </>}
        </>)
    }

    renderOldMember(): ReactElement {
        return (<>
            {(this.props.roleId === ROLE_FOR_GUEST.OLD_MEMBER) && this.props.location.state.accessExpirationDate &&
                <>
                    <Box height={'15px'}/>
                    <CompleteMessageSub>
                        申請内容は{formatDate(this.props.location.state.accessExpirationDate)}までご確認いただけます。
                    </CompleteMessageSub>
                </>}
        </>)
    }

    renderNewMember(): ReactElement {
        if (this.props.roleId !== ROLE_FOR_GUEST.NEW_MEMBER) {
            return (<></>)
        }

        return (<>
            <Box height={'15px'}/>
            <CompleteMessageSub>
                引き続き、マンション居住者専用アプリ「素敵ネット」の<br/>
                ユーザ登録をお願いいたします。<br/>
                {!this.state.isPossibleSutekiNetRegister &&
                    '旧組合員様の提出が完了すると、ユーザ登録が可能となります。'}
            </CompleteMessageSub>
        </>)
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (<>
            <ColumnFlexContainer>
                <BlockSpacer height={'60px'}/>
                <img src={'/resources/images/icons/icon_reserve_complete.svg'} width={'120px'} alt={'complete'}/>
                <BlockSpacer height={'15px'}/>
                <CompleteMessage>申請を提出しました</CompleteMessage>
                {this.renderIntermediary()}
                {this.renderOldMember()}
                {this.renderNewMember()}
                <BlockSpacer height={'30px'}/>
            </ColumnFlexContainer>
            {this.renderRegistrationButton()}
            {this.renderConfirmButton()}
        </>)
    }
}


export default createGuestWithErrorHandlingPage(NatOwnershipTransfersCompletePage)

const CompleteMessage = styled.div`
  font-size: ${FontSize.Big};
  font-weight: ${FontWeights.Bold};
  color: ${Colors.AccentDark};
`
const CompleteMessageSub = styled.div`
  font-size: ${FontSize.Small};
  text-align: center;
`
